/**
 * questLabAdmin.service.ts — Admin > Integrations > Quest, the Lab Presets and
 * Lab Codes tabs (legacy admin_quest_lab_list.php): the practice's shared
 * order presets, through /quest-presets, and its own test codes, through
 * /quest-codes. Writes need Admin Access.
 *
 * A preset's tests are saved the way legacy's admin did: through the order
 * core in admin mode (POST /lab-orders/presets, fromAdmin = 1), which writes
 * the HL7 and finds the preset by its title.
 */
import { apiClient } from '@/services/apiClient';
import { saveQuestPreset, type QuestOrderInsurance, type QuestOrderNote, type QuestOrderTest } from '@/services/labs.service';
import type { ApiResponse } from '@/types/api.types';

export interface QuestPresetRow {
  id: number;
  title: string;
  active: boolean;
  order: number;
  savedAt: string;
}

export const PRESET_TITLE_MAX = 100;

export function listQuestPresetsAdmin(): Promise<QuestPresetRow[]> {
  return apiClient.get<ApiResponse<QuestPresetRow[]>>('/quest-presets').then((r) => r.data.data ?? []);
}

export function renameQuestPreset(id: number, title: string): Promise<void> {
  return apiClient.put(`/quest-presets/${id}`, { title: title.trim() }).then(() => undefined);
}

export function deleteQuestPreset(id: number): Promise<void> {
  return apiClient.delete(`/quest-presets/${id}`).then(() => undefined);
}

export function restoreQuestPreset(id: number): Promise<void> {
  return apiClient.post(`/quest-presets/${id}/restore`).then(() => undefined);
}

export function reorderQuestPresets(ids: number[]): Promise<void> {
  return apiClient.post('/quest-presets/reorder', { ids }).then(() => undefined);
}

/** What a preset holds besides its tests; kept as stored when the tests are saved again. */
export interface PresetExtras {
  facility: string;
  provider: string;
  pidNotes: QuestOrderNote[];
  insurance: QuestOrderInsurance[];
  diagnoses: string[];
}

export const NO_EXTRAS: PresetExtras = { facility: '', provider: '', pidNotes: [], insurance: [], diagnoses: [] };

/**
 * Saves a shared preset's tests under `title` (a new preset, or the shared
 * preset that already has this title). Patient 0, as legacy's admin mode.
 */
export function saveQuestPresetTests(title: string, tests: QuestOrderTest[], extras: PresetExtras): Promise<unknown> {
  const order = {
    patientId: 0,
    facility: extras.facility,
    provider: extras.provider,
    businessUnit: 'Default',
    pscHold: false,
    firstName: '',
    middleName: '',
    lastName: '',
    birthMonth: '',
    birthDay: '',
    birthYear: '',
    street: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    phone: '',
    gender: '',
    tests,
    pidNotes: extras.pidNotes,
    insurance: extras.insurance,
    diagnoses: extras.diagnoses,
    internalNotes: [],
    externalNotes: [],
    presetTitle: title.trim(),
    presetShared: '1',
    // Legacy's admin mode: the core finds the shared preset by title.
    fromAdmin: '1',
  };
  return saveQuestPreset(order);
}

// ─── Custom codes ───────────────────────────────────────────────────────────

export interface QuestCustomCode {
  code: string;
  description: string;
  specimenType: string;
  state: string;
}

export const QUEST_CODE_MAX = 16;
export const QUEST_DESCRIPTION_MAX = 175;

/** Legacy's Specimen Type choices. */
export const SPECIMEN_TYPES = [
  'AMNIOTIC', 'BACTERIOLOGY', 'BLOOD', 'CSF', 'FLUID', 'GASTRIC', 'HAIR', 'LAVENDER', 'NAILS', 'OTHER',
  'PERITONEAL FLUID', 'PLASMA-UNSPECIFIED VIAL POUR', 'RANDOM', 'SALIVA', 'SERUM', 'SPUTUM', 'STOOL/FECES',
  'SYNOVIAL FLUID', 'TISSUE', 'URINE', 'VARIED', 'WATER',
];

/** Legacy's Specimen State choices (it split requisitions on these). */
export const SPECIMEN_STATES: { value: string; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'FZ', label: 'Frozen' },
  { value: 'RF', label: 'Refrigerated' },
  { value: 'RT', label: 'Room Temperature' },
];

export function stateLabel(state: string): string {
  return SPECIMEN_STATES.find((s) => s.value === state)?.label ?? state;
}

export function listQuestCodes(): Promise<QuestCustomCode[]> {
  return apiClient.get<ApiResponse<QuestCustomCode[]>>('/quest-codes').then((r) => r.data.data ?? []);
}

export function createQuestCode(c: QuestCustomCode): Promise<void> {
  return apiClient.post('/quest-codes', { ...c, code: c.code.trim(), description: c.description.trim() }).then(() => undefined);
}

export function updateQuestCode(code: string, c: Omit<QuestCustomCode, 'code'>): Promise<void> {
  return apiClient
    .put(`/quest-codes/${encodeURIComponent(code)}`, { ...c, description: c.description.trim() })
    .then(() => undefined);
}

export function deleteQuestCode(code: string): Promise<void> {
  return apiClient.delete(`/quest-codes/${encodeURIComponent(code)}`).then(() => undefined);
}
